<script setup lang="ts">
import WrIcon from "../global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface WhyUsItem {
  title: string;
  description: string;
  svgIcon?: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    items: WhyUsItem[];
    eyebrow?: string;
  }>(),
  {
    eyebrow: "Why us",
  },
);

/* Split "150+ apps shipped" → { value: 150, suffix: "+", label: "apps shipped" } */
const parseStat = (title: string) => {
  const m = title.match(/^(\d+(?:\.\d+)?)(\+|%)/);
  if (!m) return { value: null as number | null, suffix: "", label: title };
  return { value: parseFloat(m[1]), suffix: m[2], label: title.slice(m[0].length).trim() };
};

const stats = computed(() => props.items.map((i) => parseStat(i.title)));

const counts = ref<number[]>(props.items.map(() => 0));
const sectionEl = ref<HTMLElement | null>(null);

let observer: IntersectionObserver | null = null;
let rafId = 0;

onMounted(() => {
  if (!sectionEl.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer?.disconnect();
      // Respect the user's reduced-motion setting: jump straight to the final values
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        stats.value.forEach((s, i) => {
          if (s.value != null) counts.value[i] = s.value;
        });
        return;
      }
      const duration = 1400;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        stats.value.forEach((s, i) => {
          if (s.value != null) counts.value[i] = Math.round(s.value * eased);
        });
        if (t < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    },
    { threshold: 0.3 },
  );
  observer.observe(sectionEl.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  cancelAnimationFrame(rafId);
});
</script>

<template>
  <section ref="sectionEl" class="relative">
    <div class="text-center">
      <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />{{ eyebrow }}</span>
      <h2 class="fl-heading">{{ heading }}</h2>
      <p class="fl-subcopy" :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'">{{ subcopy }}</p>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mt-10 lg:mt-14 px-4 max-w-[1100px] mx-auto">
      <div v-for="(item, i) in items" :key="item.title" class="fl-stat">
          <div class="fl-stat-top">
            <template v-if="stats[i]?.value != null">
              <span class="fl-stat-num" :aria-label="`${stats[i]?.value}${stats[i]?.suffix} ${stats[i]?.label}`">
                <span class="fl-stat-count" aria-hidden="true">{{ counts[i] }}</span><span class="fl-stat-suffix" aria-hidden="true">{{ stats[i]?.suffix }}</span>
              </span>
            </template>
            <div v-else class="fl-stat-icon">
              <WrIcon :name="item.svgIcon || 'streamline:trophy'" class="size-7 lg:size-8" :class="isLight ? 'text-[#501D71]' : 'text-[#BA55FE]'" />
            </div>
          </div>
          <h3 class="fl-stat-label">{{ stats[i]?.label || item.title }}</h3>
          <p class="fl-stat-desc">{{ item.description }}</p>
        </div>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #ba55fe; box-shadow: 0 0 8px rgba(186, 85, 254, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(28px, 4vw, 48px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

.fl-stat {
  position: relative; overflow: hidden; height: 100%;
  border-radius: 16px; border: 1px solid rgba(136, 127, 255, 0.15);
  background: rgba(18, 0, 44, 0.35); padding: 22px 20px; text-align: center;
  transition: transform 0.35s ease, border-color 0.35s ease, background 0.35s ease, box-shadow 0.35s ease;
}
@media (min-width: 1024px) { .fl-stat { padding: 26px 22px; } }
.fl-stat:hover {
  transform: translateY(-4px);
  border-color: rgba(186, 85, 254, 0.4);
  background: rgba(18, 0, 44, 0.55);
  box-shadow: 0 24px 44px -22px rgba(186, 85, 254, 0.45);
}
.fl-stat::after {
  content: ""; position: absolute; inset: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.09) 50%, transparent 60%);
  transform: translateX(-130%); transition: transform 0.75s ease; pointer-events: none;
}
.fl-stat:hover::after { transform: translateX(130%); }

.fl-stat-top { display: flex; justify-content: center; margin-bottom: 14px; min-height: 56px; align-items: center; }
.fl-stat-num {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(40px, 4.5vw, 56px); font-weight: 600; line-height: 1;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-stat-suffix { font-size: 0.7em; }
.fl-stat-icon {
  width: 56px; height: 56px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(186, 85, 254, 0.25); background: rgba(186, 85, 254, 0.08);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}
.fl-stat:hover .fl-stat-icon { transform: scale(1.08) rotate(-4deg); box-shadow: 0 0 24px rgba(186, 85, 254, 0.3); }

.fl-stat-label { font-size: 16px; font-weight: 600; color: #fff; }
@media (min-width: 1024px) { .fl-stat-label { font-size: 18px; } }
.fl-stat-desc { font-size: 13px; line-height: 1.6; color: #bcb2c3; margin-top: 6px; }
@media (min-width: 1024px) { .fl-stat-desc { font-size: 14px; } }

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-stat {
  background: #fff;
  border-color: rgba(208, 208, 208, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
html:not(.my-app-dark) .fl-stat:hover {
  background: #fff;
  border-color: rgba(186, 85, 254, 0.35);
  box-shadow: 0 8px 24px rgba(166, 85, 246, 0.15);
}
html:not(.my-app-dark) .fl-stat-icon {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  border-color: transparent;
}
html:not(.my-app-dark) .fl-stat-label { color: #613faa; }
html:not(.my-app-dark) .fl-stat-desc { color: #333; }
</style>
